import React from 'react';
import { alpha } from '@mui/material/styles';

import connectionLostIcon from '@src/assets/icons/connection-lost.png';
import gpsLostIcon from '@src/assets/icons/gps-lost.png';
import { ArcAlert, ArcStack, ArcText } from '@src/core/ui';

import { MapStatusBannersProps } from './types';

/** Figma map status banner — #D13135 @ 90% */
const BANNER_BG = alpha('#D13135', 0.9);

/**
 * Sit just below top chrome (Map/Satellite, Search, Weather / camera toolbar)
 * so those controls stay clickable without a large empty gap.
 */
const BANNER_TOP_PX = 72;

/**
 * Horizontal span aligned to map chrome:
 * - start: Map | Satellite divider (~gmaps left inset + "Map" button)
 * - end: gap between Weather and Find me (toolbar right={5}=40 + FindMe 50)
 */
const BANNER_LEFT_PX = 10;
const BANNER_RIGHT_PX = 6;

const GpsLostIcon = (
  <img src={gpsLostIcon} alt='' width={20} height={20} />
);

const ConnectionLostIcon = (
  <img src={connectionLostIcon} alt='' width={20} height={20} />
);

const BANNER_SX = {
  bgcolor: BANNER_BG,
  color: 'common.white',
  pointerEvents: 'auto',
  width: '100%',
  py: 1,
  px: 1.5,
  borderRadius: 1,
  boxShadow: 2,
  alignItems: 'center',
  '& .MuiAlert-icon': {
    color: 'common.white',
    alignItems: 'center',
    py: 0,
    mr: 1,
  },
  '& .MuiAlert-message': {
    py: 0.25,
  },
} as const;

export const MapStatusBanners: React.FC<MapStatusBannersProps> = ({
  gpsLost,
  connectionLost,
}) => {
  if (!gpsLost && !connectionLost) {
    return null;
  }

  return (
    <ArcStack
      position='absolute'
      zIndex={1000}
      spacing={1}
      alignItems='stretch'
      sx={{
        top: BANNER_TOP_PX,
        left: BANNER_LEFT_PX,
        right: BANNER_RIGHT_PX,
        pointerEvents: 'none',
      }}
    >
      {gpsLost && (
        <ArcAlert severity='error' icon={GpsLostIcon} sx={BANNER_SX}>
          <ArcText.caption fontSize={13} color='common.white'>
            GPS lost. Positioning and automated flight functions may be limited.
            Manual intervention may be required.
          </ArcText.caption>
        </ArcAlert>
      )}
      {connectionLost && (
        <ArcAlert severity='error' icon={ConnectionLostIcon} sx={BANNER_SX}>
          <ArcText.caption fontSize={13} color='common.white'>
            Drone connection lost. Live updates and synchronization are unavailable.
          </ArcText.caption>
        </ArcAlert>
      )}
    </ArcStack>
  );
};
